import React from "react";
import { Col, Row } from "reactstrap";
import ModelMediaContainer from "../Media/ModelMediaContainer";

/**
 * service building block (SCRUM-553, VariableField registry) — image +
 * description pairs. Emits the full items array via `onChange(name, value)`
 * (legacy handleServiceArrayUpdate payload).
 *
 * The per-site DB-format unwrap (`[{siteid, label}]` → label) is isolated
 * HERE, per the refactor design doc §6 — hosts never see the wrapped form.
 * Like the legacy blocks there are no add/remove item buttons (their
 * handlers existed but were never rendered). The image is picker-only by
 * default; TemplateForm's create card passes `showUrlInput` to keep its
 * plain image-URL text input (characterized in SCRUM-630).
 */
export type ServiceItem = { image?: string; description?: string };

export type ServiceFieldProps = {
  settingVar: { variable: string; value?: unknown };
  onChange: (name: string, value: ServiceItem[]) => void;
  showUrlInput?: boolean;
};

export default function ServiceField({ settingVar, onChange, showUrlInput = false }: ServiceFieldProps) {
  // Handle both formats:
  // 1. Direct: [{ image, description }, ...]
  // 2. DB:     [{ siteid, label: [{ image, description }, ...] }]
  let rawValue = settingVar.value;
  if (
    Array.isArray(rawValue) &&
    rawValue.length > 0 &&
    rawValue[0]?.siteid &&
    rawValue[0]?.label !== undefined
  ) {
    rawValue = rawValue[0].label;
  }
  const items: ServiceItem[] =
    Array.isArray(rawValue) && rawValue.length > 0
      ? rawValue
      : [{ image: "", description: "" }];

  const updateItem = (idx: number, field: keyof ServiceItem, newVal: string) => {
    onChange(
      settingVar.variable,
      items.map((item, i) => (i === idx ? { ...item, [field]: newVal } : item))
    );
  };

  const onMediaSelect = (idx: number) => (selectedImage: { secure_url?: string } | null | undefined) => {
    if (selectedImage && selectedImage.secure_url) {
      updateItem(idx, "image", selectedImage.secure_url);
    }
  };

  return (
    <>
      {items.map((item, i) => (
        <div
          // index key: no add or remove in this field, so rows never shift (SCRUM-2183)
          key={i}
          className="mb-3 p-2"
          style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}
        >
          <Row className="mb-2">
            <Col md="2">
              <label style={{ color: "#597ade" }}>Image</label>
            </Col>
            {showUrlInput && (
              <Col md="3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter image URL..."
                  value={item.image || ""}
                  onChange={(e) => updateItem(i, "image", e.target.value)}
                />
              </Col>
            )}
            <Col md="3">
              <ModelMediaContainer selectedImg={onMediaSelect(i)} />
            </Col>
            <Col md={showUrlInput ? "4" : "7"}>
              {item.image && (
                <img
                  src={item.image}
                  alt={`service-${i}`}
                  style={{ maxWidth: "100%", maxHeight: 60, objectFit: "cover", borderRadius: 6 }}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col md="2">
              <label style={{ color: "#597ade" }}>Description</label>
            </Col>
            <Col md="10">
              <textarea
                rows={3}
                className="form-control"
                placeholder="Enter description..."
                value={item.description || ""}
                onChange={(e) => updateItem(i, "description", e.target.value)}
              />
            </Col>
          </Row>
        </div>
      ))}
    </>
  );
}
